import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/Badges";
import {
  Truck, Wrench, AlertTriangle, MapPin, Package, TrendingUp, Database, FileSpreadsheet, CalendarClock,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, CartesianGrid, LineChart, Line, Legend,
} from "recharts";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLORS = {
  "Aktif Çalışıyor": "#10B981",
  "Arızalı": "#EF4444",
  "Bakımda": "#F59E0B",
  "Boşta-Atıl": "#71717A",
};

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card data-testid={testid} className="rounded-sm border border-border p-5 bg-white transition-colors duration-200 hover:border-primary/50">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
          <p className="font-mono text-4xl font-bold tracking-tighter mt-3 text-zinc-950">{value}</p>
        </div>
        <div className={`w-11 h-11 rounded-sm flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [staleMaterials, setStaleMaterials] = useState([]);
  const [staleDays, setStaleDays] = useState(30);
  const [digestRecipients, setDigestRecipients] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [scorecard, setScorecard] = useState(null);
  const [scPreset, setScPreset] = useState("all");
  const [scFrom, setScFrom] = useState("");
  const [scTo, setScTo] = useState("");

  const rangeFor = (preset) => {
    const now = new Date();
    const y = now.getFullYear();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "year") return { from: `${y}-01-01`, to: `${y}-12-31` };
    if (preset === "month") return { from: iso(new Date(y, now.getMonth(), 1)), to: iso(new Date(y, now.getMonth() + 1, 0)) };
    if (preset === "last30") return { from: iso(new Date(now.getTime() - 29 * 864e5)), to: iso(now) };
    return { from: "", to: "" };
  };

  const loadScorecard = (from, to) => api.get("/reports/site-scorecard", { params: { date_from: from || undefined, date_to: to || undefined } })
    .then((r) => setScorecard(r.data)).catch(() => {});

  const applyPreset = (preset) => {
    setScPreset(preset);
    if (preset === "custom") return;
    const { from, to } = rangeFor(preset);
    setScFrom(from); setScTo(to);
    loadScorecard(from, to);
  };

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exportingSc, setExportingSc] = useState(false);

  const openDetail = async (site) => {
    setDetailOpen(true); setDetail(null); setDetailLoading(true);
    try {
      const r = await api.get("/reports/site-detail", { params: { site, date_from: scFrom || undefined, date_to: scTo || undefined } });
      setDetail(r.data);
    } catch (e) { toast.error("Detay yüklenemedi"); }
    finally { setDetailLoading(false); }
  };

  const exportScorecard = async () => {
    setExportingSc(true);
    try {
      const r = await api.get("/reports/site-scorecard/export", { params: { date_from: scFrom || undefined, date_to: scTo || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "santiye_karnesi.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Dışa aktarılamadı"); }
    finally { setExportingSc(false); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      await api.post("/maintenance/digest/send");
      toast.success("Bakım & belge özeti e-postası gönderildi");
    } catch (e) {
      toast.error("Gönderim başarısız");
    } finally { setSendingDigest(false); }
  };

  const load = async () => {
    try {
      const res = await api.get("/dashboard/summary");
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  const loadStale = (days) => api.get("/site-stock-summary", { params: { stale_days: days || undefined } })
    .then((r) => setStaleMaterials((r.data || []).filter((x) => x.is_stale))).catch(() => {});

  useEffect(() => {
    load();
    loadScorecard("", "");
    api.get("/settings").then((r) => { setStaleDays(r.data.stale_days); setAdminEmail(r.data.admin_email || ""); setDigestRecipients((r.data.digest_recipients || []).join(", ")); loadStale(r.data.stale_days); }).catch(() => loadStale());
  }, []);

  const saveThreshold = async (v) => {
    const n = parseInt(v, 10);
    if (!n || n < 1) return;
    setStaleDays(n);
    try { await api.put("/settings", { stale_days: n }); await loadStale(n); toast.success(`Uyarı eşiği ${n} gün olarak kaydedildi`); } catch (e) { toast.error("Kaydedilemedi"); }
  };

  const saveRecipients = async () => {
    setSavingRecipients(true);
    const list = digestRecipients.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
    try {
      const r = await api.put("/settings", { digest_recipients: list });
      setDigestRecipients((r.data.digest_recipients || []).join(", "));
      toast.success("Özet e-posta alıcıları kaydedildi");
    } catch (e) { toast.error("Kaydedilemedi"); }
    finally { setSavingRecipients(false); }
  };

  const seed = async () => {
    try {
      const res = await api.post("/seed");
      if (res.data.seeded) {
        toast.success("Örnek veriler yüklendi");
        load();
      } else {
        toast.info("Veriler zaten mevcut");
      }
    } catch (e) {
      toast.error("Yükleme başarısız");
    }
  };

  const exportAll = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/export/all`, { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `filo_takip_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi");
    } catch (e) {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setExporting(false);
    }
  };

  const statusData = data
    ? Object.entries(data.status_counts).map(([name, value]) => ({ name, value }))
    : [];

  const MONTHS_TR = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  const costData = (data?.monthly_cost || []).map((m) => {
    const [y, mo] = m.month.split("-");
    return {
      label: `${MONTHS_TR[parseInt(mo, 10) - 1]} ${y.slice(2)}`,
      "Periyodik Bakım": m["Periyodik Bakım"] || 0,
      "Onarım": m["Onarım"] || 0,
      "Saha Arızası": m["Saha Arızası"] || 0,
    };
  });

  return (
    <Layout
      title="Özet Panosu"
      subtitle="Filonuzun anlık durumu, kritik uyarılar ve saha dağılımı"
      actions={
        <div className="flex items-center gap-2">
          <Button data-testid="export-all-btn" onClick={exportAll} disabled={exporting} className="rounded-sm gap-2 bg-secondary hover:bg-secondary/90 text-white">
            <FileSpreadsheet className="w-4 h-4" /> {exporting ? "Hazırlanıyor..." : "Tümünü Excel'e Aktar"}
          </Button>
          {user?.role === "admin" && (
          <Button data-testid="seed-btn" onClick={seed} variant="outline" className="rounded-sm gap-2">
            <Database className="w-4 h-4" /> Örnek Veri Yükle
          </Button>
          )}
        </div>
      }
    >
      {loading ? (
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-white border border-border rounded-sm animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-4">
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi testid="kpi-total-machines" icon={Truck} label="Toplam Makine/Araç" value={data.total_machines} accent="bg-secondary/10 text-secondary" />
            <Kpi testid="kpi-maintenance" icon={Wrench} label="Arızalı / Bakımda" value={data.in_maintenance} accent="bg-amber-100 text-amber-700" />
            <Kpi testid="kpi-low-stock" icon={AlertTriangle} label="Kritik Stok Kalemi" value={data.low_stock_count} accent="bg-red-100 text-red-700" />
            <Kpi testid="kpi-active-fields" icon={MapPin} label="Aktif Saha / Lokasyon" value={data.active_fields} accent="bg-primary/10 text-primary" />
          </div>

          {/* Charts + alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2 rounded-sm border border-border p-6 bg-white">
              <h3 className="font-display font-bold text-lg mb-1">Makine Durum Dağılımı</h3>
              <p className="text-xs text-muted-foreground mb-5">Çalışma durumuna göre makine sayıları</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={statusData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E4E7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 }} />
                  <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                    {statusData.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.name] || "#71717A"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className="rounded-sm border border-border p-6 bg-white flex flex-col" data-testid="alerts-widget">
              <h3 className="font-display font-bold text-lg mb-1">Kritik Uyarılar</h3>
              <p className="text-xs text-muted-foreground mb-4">Anlık müdahale gerektiren kalemler</p>
              <div className="space-y-2 overflow-y-auto max-h-[240px] pr-1">
                {data.alerts.length === 0 && (
                  <p className="text-sm text-muted-foreground py-8 text-center">Aktif uyarı yok ✓</p>
                )}
                {data.alerts.map((a, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-sm bg-zinc-50 border-l-2" style={{ borderColor: a.severity === "destructive" ? "#EF4444" : "#F59E0B" }}>
                    <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${a.severity === "destructive" ? "text-red-500" : "text-amber-500"}`} />
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{a.type}</p>
                      <p className="text-sm text-zinc-800 leading-snug">{a.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Location distribution + cost */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2 rounded-sm border border-border p-6 bg-white">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" /> Saha Bazlı Makine Dağılımı
              </h3>
              <div className="space-y-3">
                {data.location_distribution.length === 0 && <p className="text-sm text-muted-foreground">Kayıt yok</p>}
                {data.location_distribution.map((l, i) => {
                  const max = data.location_distribution[0].count || 1;
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-zinc-800">{l.location}</span>
                        <span className="font-mono font-bold">{l.count}</span>
                      </div>
                      <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                        <div className="h-full bg-primary rounded-sm transition-all duration-500" style={{ width: `${(l.count / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <div className="space-y-4">
              <Card className="rounded-sm border border-border p-5 bg-secondary text-white">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-blue-200">
                  <TrendingUp className="w-4 h-4" /> Toplam Bakım Maliyeti
                </div>
                <p className="font-mono text-3xl font-bold mt-3">
                  {data.total_maintenance_cost.toLocaleString("tr-TR")} ₺
                </p>
              </Card>
              <Card className="rounded-sm border border-border p-5 bg-white">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                  <Package className="w-4 h-4" /> Toplam Ekipman Adedi
                </div>
                <p className="font-mono text-3xl font-bold mt-3 text-zinc-950">{data.total_equipment}</p>
              </Card>
            </div>
          </div>

          {/* Bakım Hatırlatıcıları */}
          <Card className="rounded-sm border border-border p-6 bg-white" data-testid="maintenance-reminders">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <Wrench className="w-5 h-5 text-primary" /> Bakım Hatırlatıcıları
              </h3>
              {user?.role === "admin" && data.maintenance_reminders?.length > 0 && (
                <Button data-testid="send-digest-btn" onClick={sendDigest} disabled={sendingDigest} variant="outline" size="sm" className="rounded-sm gap-2">
                  <Wrench className="w-3.5 h-3.5" /> {sendingDigest ? "Gönderiliyor..." : "Özeti E-posta Gönder"}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-4">Yaklaşan veya geciken periyodik bakımlar (çalışma saati / KM bazlı) · her sabah 07:00'de yöneticiye otomatik özet e-posta gider</p>
            {(!data.maintenance_reminders || data.maintenance_reminders.length === 0) ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Yaklaşan bakım yok ✓</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.maintenance_reminders.map((r, i) => {
                  const overdue = r.reminder_status === "overdue";
                  return (
                    <div key={i} data-testid={`reminder-${r.code}`} className={`rounded-sm border p-4 ${overdue ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-sm">{r.code}</span>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${overdue ? "bg-red-200 text-red-900" : "bg-amber-200 text-amber-900"}`}>
                          {overdue ? "Gecikti" : "Yaklaşıyor"}
                        </span>
                      </div>
                      <p className="text-sm font-medium mt-1 text-zinc-900">{r.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{r.location}</p>
                      <p className="text-sm font-mono mt-2 text-zinc-800">
                        {overdue
                          ? `${Math.abs(r.remaining)} ${r.reading_unit} aşıldı`
                          : `${r.remaining} ${r.reading_unit} kaldı`}
                        <span className="text-muted-foreground"> · hedef {r.next_due?.toLocaleString?.("tr-TR") ?? r.next_due} {r.reading_unit}</span>
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Belge & Muayene Takibi (Tarih Bazlı) */}
          <Card className="rounded-sm border border-border p-6 bg-white" data-testid="date-reminders">
            <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-primary" /> Belge & Muayene Takibi
            </h3>
            <p className="text-xs text-muted-foreground mb-4">Yaklaşan veya süresi dolan muayene, sigorta, kasko ve egzoz tarihleri (30 gün içinde)</p>
            {(!data.date_reminders || data.date_reminders.length === 0) ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Yaklaşan belge/muayene yok ✓</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.date_reminders.map((r, i) => {
                  const overdue = r.status === "overdue";
                  const disp = r.date ? r.date.split("-").reverse().join(".") : "-";
                  return (
                    <div key={i} data-testid={`date-reminder-${r.code}-${r.label}`} className={`rounded-sm border p-4 ${overdue ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-sm">{r.code}</span>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${overdue ? "bg-red-200 text-red-900" : "bg-amber-200 text-amber-900"}`}>
                          {overdue ? "Doldu" : "Yaklaşıyor"}
                        </span>
                      </div>
                      <p className="text-sm font-medium mt-1 text-zinc-900">{r.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{r.location}</p>
                      <p className="text-sm mt-2 text-zinc-800">
                        <span className="font-semibold">{r.label}</span> · {disp}
                        <span className="text-muted-foreground"> · {overdue ? `${Math.abs(r.days_left)} gün geçti` : `${r.days_left} gün kaldı`}</span>
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
            {user?.role === "admin" && (
              <div className="mt-4 border-t border-border pt-4" data-testid="digest-recipients-control">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Sabah Özet E-posta Alıcıları</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">Her sabah 07:00'de bakım + belge özeti gönderilir. Yönetici her zaman alır; ek adresleri virgülle ekleyin.</p>
                {adminEmail && (
                  <span className="inline-flex items-center rounded-sm bg-zinc-100 border border-zinc-200 px-2 py-0.5 text-[11px] font-medium text-zinc-600 mb-2">{adminEmail} · yönetici</span>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input data-testid="digest-recipients-input" value={digestRecipients} onChange={(e) => setDigestRecipients(e.target.value)} placeholder="ek adres: ornek@rayemdemiryolu.com, ikinci@..." className="rounded-sm bg-white" />
                  <Button data-testid="save-recipients-btn" onClick={saveRecipients} disabled={savingRecipients} variant="outline" className="rounded-sm shrink-0">{savingRecipients ? "Kaydediliyor..." : "Kaydet"}</Button>
                </div>
              </div>
            )}
          </Card>

          {/* Aylık Bakım Maliyeti Trendi */}
          <Card className="rounded-sm border border-border p-6 bg-white" data-testid="monthly-cost-chart">
            <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-secondary" /> Aylık Bakım Maliyeti Trendi
            </h3>
            <p className="text-xs text-muted-foreground mb-5">Aylara göre toplam bakım/onarım harcaması (₺)</p>
            {costData.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Henüz bakım kaydı yok</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={costData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E4E7" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => v.toLocaleString("tr-TR")} />
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 }} formatter={(v, n) => [`${v.toLocaleString("tr-TR")} ₺`, n]} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Periyodik Bakım" stackId="c" fill="#3B82F6" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Onarım" stackId="c" fill="#F59E0B" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Saha Arızası" stackId="c" fill="#EF4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Yenileme Adayları - En yüksek bakım maliyeti */}
          {data.cost_ranking && data.cost_ranking.length > 0 && (
            <Card className="rounded-sm border border-border p-6 bg-white" data-testid="cost-ranking">
              <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-red-600" /> Yenileme Adayları
              </h3>
              <p className="text-xs text-muted-foreground mb-4">En yüksek toplam bakım/onarım maliyetine sahip makineler — satış/yenileme değerlendirmesi için</p>
              <div className="space-y-2">
                {data.cost_ranking.map((c, i) => {
                  const max = data.cost_ranking[0].total || 1;
                  return (
                    <div key={c.code} data-testid={`ranking-${c.code}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{i + 1}. {c.name} <span className="font-mono text-muted-foreground">({c.code})</span> · {c.count} işlem</span>
                        <span className="font-mono font-bold">{c.total.toLocaleString("tr-TR")} ₺</span>
                      </div>
                      <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                        <div className="h-full bg-red-500 rounded-sm transition-all duration-500" style={{ width: `${(c.total / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
          {/* Şantiye Karnesi — tarih aralığı filtreli */}
          {scorecard && scorecard.cards && scorecard.cards.length > 0 && (
            <Card className="rounded-sm border border-border p-6 bg-white" data-testid="site-scorecard">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-1">
                <h3 className="font-display font-bold text-lg flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" /> Şantiye Karnesi
                </h3>
                <div className="flex flex-wrap items-center gap-1.5">
                  {[["all", "Tümü"], ["month", "Bu Ay"], ["year", "Bu Yıl"], ["last30", "Son 30 Gün"], ["custom", "Özel"]].map(([k, lbl]) => (
                    <button
                      key={k}
                      data-testid={`sc-preset-${k}`}
                      onClick={() => applyPreset(k)}
                      className={`px-2.5 py-1 rounded-sm text-xs font-bold border transition-colors ${scPreset === k ? "bg-primary text-white border-primary" : "bg-white text-zinc-600 border-zinc-200 hover:border-primary"}`}
                    >{lbl}</button>
                  ))}
                  <Button data-testid="sc-export-btn" onClick={exportScorecard} disabled={exportingSc} variant="outline" size="sm" className="rounded-sm gap-1.5 ml-1 h-7">
                    <FileSpreadsheet className="w-3.5 h-3.5" /> {exportingSc ? "..." : "Excel"}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Her şantiye için makine sayısı, seçili dönemdeki bakım maliyeti ve sahada bekleyen malzeme</p>
              {scPreset === "custom" && (
                <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-zinc-50 rounded-sm border border-zinc-100">
                  <div><Label className="text-[10px] uppercase font-bold text-muted-foreground">Başlangıç</Label><DatePicker testid="sc-from" value={scFrom} onChange={setScFrom} clearable placeholder="Tarih" /></div>
                  <div><Label className="text-[10px] uppercase font-bold text-muted-foreground">Bitiş</Label><DatePicker testid="sc-to" value={scTo} onChange={setScTo} clearable placeholder="Tarih" /></div>
                  <Button data-testid="sc-apply" variant="outline" className="rounded-sm" onClick={() => loadScorecard(scFrom, scTo)}>Uygula</Button>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {scorecard.cards.map((c) => (
                  <button key={c.site} type="button" data-testid={`scorecard-${c.site}`} onClick={() => openDetail(c.site)} className="text-left rounded-sm border border-border p-4 bg-zinc-50/50 hover:border-primary hover:shadow-sm transition-all cursor-pointer">
                    <div className="font-display font-bold text-base text-zinc-900 mb-3 truncate flex items-center justify-between">
                      <span className="truncate">{c.site}</span>
                      <span className="text-[10px] font-bold text-primary uppercase shrink-0 ml-2">Detay ›</span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Makine</span>
                        <span className="font-bold">{c.machine_count}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Bakım Maliyeti</span>
                        <span className="font-mono font-bold text-primary">{(c.maintenance_cost || 0).toLocaleString("tr-TR")} ₺</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground pl-5">
                        <span>{c.maintenance_count} işlem</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> Malzeme Maliyeti</span>
                        <span className="font-mono font-bold text-secondary">{(c.material_cost || 0).toLocaleString("tr-TR")} ₺</span>
                      </div>
                      <div className="flex items-center justify-between text-sm border-t border-zinc-200 pt-2">
                        <span className="text-muted-foreground flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> Bekleyen Malzeme</span>
                        <span className="font-bold">{c.pending_items} kalem</span>
                      </div>
                      <div className="flex items-center justify-between text-sm bg-primary/10 -mx-1 px-2 py-1.5 rounded-sm mt-1">
                        <span className="font-bold text-zinc-900">Toplam Maliyet</span>
                        <span className="font-mono font-black text-primary">{(c.total_cost || 0).toLocaleString("tr-TR")} ₺</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          )}
          {/* Depo Bazlı Stok Dağılımı */}
          {data.warehouse_stock && data.warehouse_stock.length > 0 && (
            <Card className="rounded-sm border border-border p-6 bg-white" data-testid="warehouse-chart">
              <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2">
                <Package className="w-5 h-5 text-secondary" /> Depo Bazlı Stok Dağılımı
              </h3>
              <p className="text-xs text-muted-foreground mb-5">Her depodaki parça çeşidi ve kritik seviyedeki parça sayısı</p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.warehouse_stock} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E4E7" />
                  <XAxis dataKey="warehouse" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={{ stroke: "#E4E4E7" }} tickLine={false} interval={0} angle={-30} textAnchor="end" height={72} tickFormatter={(v) => { if (!v) return v; const p = v.split(" - "); const s = p.length > 1 ? p[p.length - 1] : v; return s.length > 14 ? s.slice(0, 13) + "…" : s; }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="count" name="Parça Çeşidi" fill="#1E3A8A" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="critical" name="Kritik" fill="#EF4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
          {(staleMaterials.length > 0 || user?.role === "admin") && (
            <Card className="rounded-sm border border-amber-200 p-6 bg-white lg:col-span-2" data-testid="stale-materials-card">
              <div className="flex items-start justify-between gap-4 mb-1 flex-wrap">
                <h3 className="font-display font-bold text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" /> Sahada Bekleyen Malzemeler
                </h3>
                {user?.role === "admin" && (
                  <div className="flex items-center gap-2" data-testid="stale-threshold-control">
                    <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Uyarı eşiği (gün)</span>
                    <Input data-testid="stale-threshold-input" type="number" min="1" defaultValue={staleDays} key={staleDays}
                      onBlur={(e) => saveThreshold(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      className="rounded-sm w-20 h-8" />
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-4">{staleDays}+ gündür hareket görmeyen, sahada kalan malzemeler — saha şefinden durum alıp iade/tüketim girin</p>
              {staleMaterials.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Şu anda eşiği aşan bekleyen malzeme yok.</p>
              ) : (
                <div className="space-y-2">
                  {staleMaterials.slice(0, 8).map((s, i) => (
                    <div key={i} data-testid={`stale-item-${s.part_code}`} className="flex items-center justify-between border border-amber-100 bg-amber-50/60 rounded-sm px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{s.part_name} <span className="font-mono text-xs text-muted-foreground">({s.part_code})</span></p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{s.destination}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="font-mono font-bold text-emerald-700">{s.net} {s.unit}</p>
                        <p className="text-[11px] font-bold text-amber-700">{s.days_idle} gündür bekliyor</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      )}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="site-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" /> {detail?.site || "Şantiye Detayı"}
            </DialogTitle>
          </DialogHeader>
          {detailLoading && <p className="py-8 text-center text-muted-foreground text-sm">Yükleniyor...</p>}
          {detail && !detailLoading && (
            <div className="space-y-5 py-1">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-sm border border-border p-3 text-center"><div className="text-2xl font-black">{detail.machines.length}</div><div className="text-xs text-muted-foreground">Makine/Ekipman</div></div>
                <div className="rounded-sm border border-border p-3 text-center"><div className="text-xl font-black text-primary">{(detail.maintenance_cost || 0).toLocaleString("tr-TR")} ₺</div><div className="text-xs text-muted-foreground">Bakım Maliyeti</div></div>
                <div className="rounded-sm border border-border p-3 text-center"><div className="text-xl font-black text-secondary">{(detail.material_cost || 0).toLocaleString("tr-TR")} ₺</div><div className="text-xs text-muted-foreground">Malzeme Maliyeti</div></div>
                <div className="rounded-sm border border-border p-3 text-center"><div className="text-2xl font-black">{detail.materials.length}</div><div className="text-xs text-muted-foreground">Malzeme Kalemi</div></div>
              </div>

              <div>
                <h4 className="font-bold text-sm uppercase tracking-wide mb-2 flex items-center gap-1.5"><Truck className="w-4 h-4" /> Makine / Ekipman ({detail.machines.length})</h4>
                {detail.machines.length === 0 ? <p className="text-sm text-muted-foreground">Bu sahada makine yok.</p> : (
                  <Table><TableHeader><TableRow className="bg-zinc-50"><TableHead className="text-xs">Kod</TableHead><TableHead className="text-xs">Ad</TableHead><TableHead className="text-xs">Kategori</TableHead><TableHead className="text-xs">Durum</TableHead></TableRow></TableHeader>
                    <TableBody>{detail.machines.map((m) => (<TableRow key={m.id}><TableCell className="font-mono font-semibold text-sm">{m.code}</TableCell><TableCell className="text-sm">{m.name}</TableCell><TableCell className="text-xs text-muted-foreground">{m.category || m.type}</TableCell><TableCell><StatusBadge status={m.status} /></TableCell></TableRow>))}</TableBody>
                  </Table>
                )}
              </div>

              <div>
                <h4 className="font-bold text-sm uppercase tracking-wide mb-2 flex items-center gap-1.5"><Wrench className="w-4 h-4" /> Bakım / Onarım ({detail.maintenance.length})</h4>
                {detail.maintenance.length === 0 ? <p className="text-sm text-muted-foreground">Seçili dönemde bakım kaydı yok.</p> : (
                  <Table><TableHeader><TableRow className="bg-zinc-50"><TableHead className="text-xs">Makine</TableHead><TableHead className="text-xs">İşlem</TableHead><TableHead className="text-xs">Tarih</TableHead><TableHead className="text-right text-xs">Maliyet</TableHead></TableRow></TableHeader>
                    <TableBody>{detail.maintenance.map((l) => (<TableRow key={l.id}><TableCell className="font-mono text-sm">{l.machine_code}</TableCell><TableCell className="text-sm">{l.operation_type} — {l.work_done}</TableCell><TableCell className="font-mono text-xs">{l.date ? l.date.split("-").reverse().join(".") : "-"}</TableCell><TableCell className="text-right font-mono font-semibold text-sm">{(l.cost || 0).toLocaleString("tr-TR")} ₺</TableCell></TableRow>))}</TableBody>
                  </Table>
                )}
              </div>

              <div>
                <h4 className="font-bold text-sm uppercase tracking-wide mb-2 flex items-center gap-1.5"><Package className="w-4 h-4" /> Sahaya Gönderilen Malzeme ({detail.materials.length})</h4>
                {detail.materials.length === 0 ? <p className="text-sm text-muted-foreground">Bu sahaya malzeme gönderilmemiş.</p> : (
                  <Table><TableHeader><TableRow className="bg-zinc-50"><TableHead className="text-xs">Malzeme</TableHead><TableHead className="text-right text-xs">Gönderilen</TableHead><TableHead className="text-right text-xs">Kullanılan</TableHead><TableHead className="text-right text-xs">İade</TableHead><TableHead className="text-right text-xs">Kalan</TableHead><TableHead className="text-right text-xs">Tüketim ₺</TableHead></TableRow></TableHeader>
                    <TableBody>{detail.materials.map((mt) => (
                      <TableRow key={mt.part_code} data-testid={`material-row-${mt.part_code}`} className={mt.is_stale ? "bg-red-50" : ""}>
                        <TableCell className="text-sm">
                          <span className="font-mono text-xs text-muted-foreground mr-1.5">{mt.part_code}</span>{mt.part_name}
                          {mt.is_stale && <span className="ml-2 inline-flex items-center rounded-sm bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 text-[10px] font-bold" title={`Sahada ${mt.days_waiting} gündür bekliyor`}>⚠ {mt.days_waiting}g bekliyor</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{mt.sent.toLocaleString("tr-TR")} {mt.unit}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-secondary">{mt.consumed.toLocaleString("tr-TR")}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{mt.returned.toLocaleString("tr-TR")}</TableCell>
                        <TableCell className={`text-right font-mono text-sm font-bold ${mt.is_stale ? "text-red-600" : ""}`}>{mt.net.toLocaleString("tr-TR")}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{(mt.consumed_cost || 0).toLocaleString("tr-TR")} ₺</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">Kırmızı satır: sahada {detail.stale_days} günden uzun süredir bekleyen malzeme. Tüketim ₺ = kullanılan miktar × birim fiyat.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
